import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  RefreshCw, Download, Search, CheckCircle2, Plus,
  Loader2, Trash2, Play, BookmarkPlus, Layers, Timer,
  ChevronRight, ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export const PublitasSyncTab = ({ token }) => {
  const [url, setUrl] = useState('');
  const [brandName, setBrandName] = useState('');
  const [collectionName, setCollectionName] = useState('');
  const [targetCollectionId, setTargetCollectionId] = useState('');
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [jobStatus, setJobStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [savedCatalogs, setSavedCatalogs] = useState([]);
  const [collections, setCollections] = useState([]);
  const [selectedCatalogIds, setSelectedCatalogIds] = useState(new Set());
  const [textureSearch, setTextureSearch] = useState('');
  const [textureResults, setTextureResults] = useState(null);
  const [searchingTextures, setSearchingTextures] = useState(false);
  const pollRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const headers = { Authorization: `Bearer ${token}` };

  const fetchHistory = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/sync-publitas/history`, { headers });
      setHistory(data);
    } catch {}
  }, []);

  const fetchSavedCatalogs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/saved-catalogs`, { headers });
      setSavedCatalogs(data);
    } catch {}
  }, []);

  const fetchCollections = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/collections-list`, { headers });
      setCollections(data);
    } catch {}
  }, []);

  useEffect(() => {
    fetchHistory();
    fetchSavedCatalogs();
    fetchCollections();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const handlePreview = async () => {
    if (!url.includes('publitas.com')) {
      toast.error('Please enter a valid Publitas catalog URL');
      return;
    }
    setPreviewing(true);
    setPreview(null);
    try {
      const { data } = await axios.post(`${API}/api/admin/sync-publitas/preview`, { url }, { headers });
      setPreview(data);
      toast.success(`Found ${data.total_textures} fabric textures`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to scan catalog');
    } finally {
      setPreviewing(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    setJobStatus(null);
    try {
      const { data } = await axios.post(`${API}/api/admin/sync-publitas`, {
        url, brand_name: brandName, collection_name: collectionName,
        target_collection_id: targetCollectionId || null,
      }, { headers });
      toast.success('Sync started!');
      startPolling(data.job_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start sync');
      setSyncing(false);
    }
  };

  const handleMultiSync = async () => {
    if (selectedCatalogIds.size === 0) {
      toast.error('Select at least one saved catalog');
      return;
    }
    setSyncing(true);
    setJobStatus(null);
    try {
      const { data } = await axios.post(`${API}/api/admin/sync-publitas/multi`, {
        catalog_ids: Array.from(selectedCatalogIds),
      }, { headers });
      toast.success(`Syncing ${data.catalog_count} catalogs...`);
      startPolling(data.job_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start multi-sync');
      setSyncing(false);
    }
  };

  const handleSaveCatalog = async () => {
    if (!url.includes('publitas.com')) {
      toast.error('Enter a valid Publitas URL first');
      return;
    }
    try {
      await axios.post(`${API}/api/admin/saved-catalogs`, {
        url, brand_name: brandName, collection_name: collectionName,
        target_collection_id: targetCollectionId || null,
      }, { headers });
      toast.success('Catalog saved!');
      fetchSavedCatalogs();
      setUrl('');
      setBrandName('');
      setCollectionName('');
      setTargetCollectionId('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  const handleDeleteCatalog = async (id) => {
    try {
      await axios.delete(`${API}/api/admin/saved-catalogs/${id}`, { headers });
      setSavedCatalogs(prev => prev.filter(c => c.id !== id));
      selectedCatalogIds.delete(id);
      setSelectedCatalogIds(new Set(selectedCatalogIds));
    } catch {}
  };

  const handleToggleAutoSync = async (id, e) => {
    e.stopPropagation();
    try {
      const { data } = await axios.patch(`${API}/api/admin/saved-catalogs/${id}/auto-sync`, {}, { headers });
      setSavedCatalogs(prev => prev.map(c => c.id === id ? { ...c, auto_sync: data.auto_sync } : c));
      toast.success(data.auto_sync ? 'Auto-sync enabled — syncs weekly' : 'Auto-sync disabled');
    } catch {
      toast.error('Failed to toggle auto-sync');
    }
  };

  const toggleCatalogSelection = (id) => {
    const next = new Set(selectedCatalogIds);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelectedCatalogIds(next);
  };

  const selectAllCatalogs = () => {
    if (selectedCatalogIds.size === savedCatalogs.length) {
      setSelectedCatalogIds(new Set());
    } else {
      setSelectedCatalogIds(new Set(savedCatalogs.map(c => c.id)));
    }
  };

  const startPolling = (id) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/api/admin/sync-publitas/status/${id}`, { headers });
        setJobStatus(data);
        if (data.status === 'completed' || data.status === 'failed') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setSyncing(false);
          fetchHistory();
          if (data.status === 'completed') {
            toast.success(`Done! ${data.synced} new, ${data.updated} updated${data.mapped_to_collection ? `, ${data.mapped_to_collection} mapped` : ''}`);
          } else {
            toast.error(`Sync failed: ${data.error}`);
          }
        }
      } catch {
        clearInterval(pollRef.current);
        pollRef.current = null;
        setSyncing(false);
      }
    }, 2000);
  };

  return (
    <div className="space-y-6" data-testid="publitas-sync-tab">
      {/* ── Add Catalog Section ── */}
      <div className="rounded-xl border border-neutral-200 bg-white p-6">
        <h3 className="text-lg font-semibold text-neutral-900 mb-1">Catalog Texture Sync</h3>
        <p className="text-sm text-neutral-500 mb-5">
          Paste a Publitas digital catalog link to extract exact fabric textures. Save catalogs to re-sync anytime in one click.
        </p>

        <div className="space-y-4">
          <div>
            <Label htmlFor="publitas-url">Publitas Catalog URL</Label>
            <Input
              id="publitas-url"
              data-testid="publitas-url-input"
              placeholder="https://view.publitas.com/coulisse-usa/usa-screen-textile-roller-collection/page/1"
              value={url}
              onChange={e => setUrl(e.target.value)}
              className="mt-1"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label htmlFor="brand-name">Brand Name</Label>
              <Input id="brand-name" data-testid="sync-brand-name" placeholder="Auto-detected"
                value={brandName} onChange={e => setBrandName(e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label htmlFor="collection-name">Collection Name</Label>
              <Input id="collection-name" data-testid="sync-collection-name" placeholder="Auto-detected"
                value={collectionName} onChange={e => setCollectionName(e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label>Map to Collection</Label>
              <select
                data-testid="target-collection-select"
                value={targetCollectionId}
                onChange={e => setTargetCollectionId(e.target.value)}
                className="w-full mt-1 rounded-md border border-neutral-300 p-2 text-sm"
              >
                <option value="">Don't map (save to library only)</option>
                {collections.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.brand_name} → {c.name} ({c.fabric_type})
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex gap-3">
            <Button onClick={handlePreview} disabled={!url || previewing || syncing}
              variant="outline" data-testid="preview-catalog-btn">
              {previewing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
              {previewing ? 'Scanning...' : 'Preview'}
            </Button>
            <Button onClick={handleSync} disabled={!url || syncing}
              className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="start-sync-btn">
              {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              {syncing ? 'Syncing...' : 'Sync & Save'}
            </Button>
            <Button onClick={handleSaveCatalog} disabled={!url} variant="outline" data-testid="save-catalog-btn">
              <BookmarkPlus className="w-4 h-4 mr-2" /> Save Catalog
            </Button>
          </div>
        </div>
      </div>

      {/* ── Saved Catalogs (Multi-Sync) ── */}
      {savedCatalogs.length > 0 && (
        <div className="rounded-xl border border-neutral-200 bg-white p-6" data-testid="saved-catalogs-section">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-semibold text-neutral-900 flex items-center gap-2">
                <Layers className="w-4 h-4" /> Saved Catalogs ({savedCatalogs.length})
              </h3>
              <p className="text-xs text-neutral-500 mt-0.5">Select catalogs and sync them all at once</p>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={selectAllCatalogs} data-testid="select-all-catalogs">
                {selectedCatalogIds.size === savedCatalogs.length ? 'Deselect All' : 'Select All'}
              </Button>
              <Button size="sm" onClick={handleMultiSync}
                disabled={selectedCatalogIds.size === 0 || syncing}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="multi-sync-btn">
                {syncing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Play className="w-4 h-4 mr-1" />}
                Sync Selected ({selectedCatalogIds.size})
              </Button>
            </div>
          </div>
          <div className="space-y-2">
            {savedCatalogs.map(cat => (
              <div
                key={cat.id}
                className={`flex items-center gap-3 p-3 rounded-lg border transition-colors cursor-pointer ${
                  selectedCatalogIds.has(cat.id) ? 'bg-amber-50 border-amber-300' : 'bg-neutral-50 border-neutral-100 hover:border-neutral-300'
                }`}
                onClick={() => toggleCatalogSelection(cat.id)}
                data-testid={`saved-catalog-${cat.id}`}
              >
                <input
                  type="checkbox"
                  checked={selectedCatalogIds.has(cat.id)}
                  onChange={() => toggleCatalogSelection(cat.id)}
                  className="w-4 h-4 rounded border-neutral-300 text-amber-600 focus:ring-amber-500"
                  onClick={e => e.stopPropagation()}
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-neutral-800 truncate">{cat.label}</p>
                  <p className="text-xs text-neutral-400 truncate">{cat.url}</p>
                </div>
                {cat.brand_name && (
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded flex-shrink-0">{cat.brand_name}</span>
                )}
                {cat.target_collection_id && (
                  <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded flex-shrink-0">Mapped</span>
                )}
                <button
                  onClick={e => handleToggleAutoSync(cat.id, e)}
                  className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors flex-shrink-0 ${
                    cat.auto_sync
                      ? 'bg-violet-100 text-violet-700 hover:bg-violet-200'
                      : 'bg-neutral-100 text-neutral-400 hover:bg-neutral-200'
                  }`}
                  title={cat.auto_sync ? 'Auto-sync enabled (weekly)' : 'Click to enable weekly auto-sync'}
                  data-testid={`auto-sync-toggle-${cat.id}`}
                >
                  <Timer className="w-3.5 h-3.5" />
                  {cat.auto_sync ? 'Weekly' : 'Off'}
                </button>
                <button
                  onClick={e => { e.stopPropagation(); handleDeleteCatalog(cat.id); }}
                  className="p-1 text-neutral-400 hover:text-red-500 transition-colors flex-shrink-0"
                  data-testid={`delete-catalog-${cat.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Global Texture Search ── */}
      <TextureSearchPanel token={token} headers={headers} />

      {/* ── Job Status ── */}
      {jobStatus && (
        <div className={`rounded-xl border p-5 ${
          jobStatus.status === 'completed' ? 'border-emerald-200 bg-emerald-50' :
          jobStatus.status === 'failed' ? 'border-red-200 bg-red-50' :
          'border-amber-200 bg-amber-50'
        }`} data-testid="sync-job-status">
          <div className="flex items-center gap-2 mb-2">
            {jobStatus.status === 'completed' ? (
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            ) : jobStatus.status === 'failed' ? (
              <RefreshCw className="w-5 h-5 text-red-600" />
            ) : (
              <Loader2 className="w-5 h-5 text-amber-600 animate-spin" />
            )}
            <span className="font-semibold text-sm capitalize">{jobStatus.status}</span>
          </div>
          <p className="text-sm text-neutral-700 mb-2">{jobStatus.progress}</p>
          {jobStatus.total_found > 0 && (
            <div className="flex gap-4 text-xs text-neutral-600">
              <span>Found: {jobStatus.total_found}</span>
              <span>Processed: {jobStatus.processed}/{jobStatus.total_found}</span>
              <span className="text-emerald-700">New: {jobStatus.synced}</span>
              <span className="text-blue-700">Updated: {jobStatus.updated}</span>
              {jobStatus.mapped_to_collection > 0 && (
                <span className="text-violet-700">Mapped: {jobStatus.mapped_to_collection}</span>
              )}
              {jobStatus.errors > 0 && <span className="text-red-700">Errors: {jobStatus.errors}</span>}
            </div>
          )}
          {jobStatus.status === 'running' && jobStatus.total_found > 0 && (
            <div className="mt-3 h-2 rounded-full bg-neutral-200 overflow-hidden">
              <div className="h-full bg-amber-500 rounded-full transition-all duration-500"
                style={{ width: `${Math.round((jobStatus.processed / jobStatus.total_found) * 100)}%` }} />
            </div>
          )}
        </div>
      )}

      {/* ── Preview Grid ── */}
      {preview && (
        <div className="rounded-xl border border-neutral-200 bg-white p-6" data-testid="preview-results">
          <div className="mb-4">
            <h3 className="font-semibold text-neutral-900">{preview.catalog_title}</h3>
            <p className="text-sm text-neutral-500">{preview.total_textures} textures across {preview.num_pages} pages</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3 max-h-[480px] overflow-y-auto">
            {preview.textures.map((tex, i) => (
              <div key={tex.webshop_id || i} className="group relative rounded-lg border border-neutral-100 overflow-hidden bg-neutral-50">
                <div className="aspect-square bg-neutral-100 overflow-hidden">
                  <img src={tex.texture_url} alt={tex.color_name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                    loading="lazy" onError={e => { e.target.style.display = 'none'; }} />
                </div>
                <div className="p-2">
                  <p className="text-xs font-medium text-neutral-800 truncate">{tex.color_name}</p>
                  <p className="text-[10px] text-neutral-400 truncate">{tex.webshop_id}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Synced Results ── */}
      {jobStatus?.status === 'completed' && jobStatus.results?.length > 0 && (
        <div className="rounded-xl border border-neutral-200 bg-white p-6" data-testid="synced-results">
          <h3 className="font-semibold text-neutral-900 mb-4">Synced Textures ({jobStatus.results.length})</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3 max-h-[480px] overflow-y-auto">
            {jobStatus.results.map((r, i) => (
              <div key={r.code || i} className="rounded-lg border border-emerald-100 overflow-hidden bg-emerald-50/30">
                <div className="aspect-square bg-neutral-100 overflow-hidden">
                  <img src={r.image_url?.startsWith('/') ? `${API}${r.image_url}` : r.image_url}
                    alt={r.name} className="w-full h-full object-cover" loading="lazy"
                    onError={e => { e.target.style.display = 'none'; }} />
                </div>
                <div className="p-2">
                  <p className="text-xs font-medium text-neutral-800 truncate">{r.name}</p>
                  <p className="text-[10px] text-neutral-400 truncate">{r.code}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Sync History ── */}
      {history.length > 0 && (
        <div className="rounded-xl border border-neutral-200 bg-white p-6" data-testid="sync-history">
          <h3 className="font-semibold text-neutral-900 mb-3">Recent Syncs</h3>
          <div className="space-y-2">
            {history.map((run, i) => (
              <div key={run.job_id || i} className="flex items-center justify-between py-2 px-3 rounded-lg bg-neutral-50 text-sm">
                <div className="flex items-center gap-3 min-w-0">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="font-medium text-neutral-800 truncate">{run.brand_name} — {run.collection_name}</p>
                    <p className="text-xs text-neutral-400">{new Date(run.completed_at).toLocaleString()}</p>
                  </div>
                </div>
                <div className="flex gap-3 text-xs text-neutral-500 flex-shrink-0">
                  <span>{run.total_found} found</span>
                  <span className="text-emerald-600">{run.synced} new</span>
                  <span className="text-blue-600">{run.updated} updated</span>
                  {run.mapped_to_collection > 0 && (
                    <span className="text-violet-600">{run.mapped_to_collection} mapped</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/* ── Global Texture Search Panel ── */
const TextureSearchPanel = ({ token, headers }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const debounceRef = useRef(null);

  const doSearch = async (q) => {
    setLoading(true);
    try {
      const params = q ? { search: q } : {};
      const { data } = await axios.get(`${API}/api/admin/publitas-textures`, { headers, params });
      const list = data.textures || data;
      setResults(list);
      setTotal(data.total ?? list.length);
    } catch {
      toast.error('Failed to search textures');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(val), 350);
  };

  // Load all textures on mount
  useEffect(() => { doSearch(''); }, []);

  // Group by collection
  const grouped = useMemo(() => {
    if (!results) return [];
    const map = {};
    results.forEach(t => {
      const key = t.collection_name || 'Unknown';
      if (!map[key]) map[key] = [];
      map[key].push(t);
    });
    return Object.entries(map).sort((a, b) => a[0].localeCompare(b[0]));
  }, [results]);

  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-6" data-testid="texture-search-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-semibold text-neutral-900 flex items-center gap-2">
            <Search className="w-5 h-5 text-amber-600" />
            Texture Library
          </h3>
          <p className="text-xs text-neutral-500 mt-0.5">
            {total} synced textures{query && ` matching "${query}"`}
          </p>
        </div>
      </div>
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
        <Input
          value={query}
          onChange={handleChange}
          placeholder="Search all textures by name, code, or collection..."
          className="pl-9"
          data-testid="texture-global-search"
        />
        {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-amber-500" />}
      </div>

      {results && results.length === 0 && (
        <p className="text-sm text-neutral-400 text-center py-8">No textures found</p>
      )}

      {grouped.length > 0 && (
        <div className="space-y-4 max-h-[600px] overflow-y-auto pr-1">
          {grouped.map(([collName, textures]) => (
            <TextureCollectionGroup key={collName} name={collName} textures={textures} />
          ))}
        </div>
      )}
    </div>
  );
};

/* Collapsible group of textures by collection */
const TextureCollectionGroup = ({ name, textures }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-neutral-100 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-4 py-2.5 bg-neutral-50 hover:bg-neutral-100 transition-colors text-left"
        data-testid={`texture-group-${name.toLowerCase().replace(/\s+/g, '-')}`}
      >
        {open
          ? <ChevronDown className="w-4 h-4 text-neutral-500" />
          : <ChevronRight className="w-4 h-4 text-neutral-500" />}
        <Layers className="w-4 h-4 text-amber-600" />
        <span className="font-medium text-neutral-800 text-sm">{name}</span>
        <span className="text-xs text-neutral-400">({textures.length})</span>
        {/* Mini swatches */}
        <div className="flex gap-0.5 ml-auto">
          {textures.slice(0, 8).map(t => (
            <div key={t.id} className="w-5 h-5 rounded border border-neutral-200 overflow-hidden">
              <img src={t.image_url?.startsWith('/') ? `${API}${t.image_url}` : t.image_url}
                alt="" className="w-full h-full object-cover" loading="lazy"
                onError={e => { e.target.style.opacity = '0'; }} />
            </div>
          ))}
          {textures.length > 8 && <span className="text-[10px] text-neutral-400 self-center ml-1">+{textures.length - 8}</span>}
        </div>
      </button>
      {open && (
        <div className="px-3 py-3 border-t border-neutral-100 bg-white">
          <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-8 gap-2">
            {textures.map(tex => (
              <div key={tex.id} className="group relative rounded-lg border border-neutral-100 overflow-hidden bg-neutral-50 hover:shadow-md transition-shadow">
                <div className="aspect-square bg-neutral-100 overflow-hidden">
                  <img
                    src={tex.image_url?.startsWith('/') ? `${API}${tex.image_url}` : tex.image_url}
                    alt={tex.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform"
                    loading="lazy"
                    onError={e => { e.target.style.display = 'none'; }}
                  />
                </div>
                <div className="p-1.5">
                  <p className="text-[11px] font-medium text-neutral-800 truncate">{tex.name}</p>
                  <p className="text-[9px] text-neutral-400 truncate">{tex.code}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PublitasSyncTab;
